import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useMutation } from 'react-query';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { IntegrationDTO, StatusDTO } from '../../api/useIntegrationsSettingsQuery';
import { IntegrationInfo } from '../IntegrationInfo/IntegrationInfo';
import { authFetch } from '../../../../../CommonUtils/authFetch';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { useDisconnectIntegration } from '../../api/useDisconnectIntegrationMutation';
import { Link } from '../../../../../CommonComponents/OPL/Link/Link';
import { queryClient } from '../../../../../../config/queryClient';
import { queryKeys } from '../../api/queryKeys';

const website = 'https://www.blueleaf.com/';
const helpUrl =
  'https://orionadvisorservices.my.site.com/OrionSupportApp/s/article/How-to-View-Blueleaf-Clients-in-Orion-Planning';

interface Props {
  integration: IntegrationDTO;
  onClose(): void;
}

const requiredField = yup
  .string()
  .nullable()
  .required();

const validation = yup.object().shape({
  username: requiredField,
});

interface FormValues {
  username: string;
  name: string;
}

export const Blueleaf = ({ integration, onClose }: Props) => {
  const { register, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      username: '',
      name: 'Blueleaf',
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: connectIntegration,
    error: connectError,
    isLoading: isConnecting,
  } = useMutation(async ({ username, name }: FormValues) => {
    const body = new FormData();

    body.append('Username', username);
    body.append('Name', name);

    return authFetch('/Adviser/Settings/SaveIntegration', {
      method: 'POST',
      body,
    });
  });

  const onSubmit = handleSubmit(async data => {
    try {
      await connectIntegration(data);

      queryClient.invalidateQueries(queryKeys.integrations());
      onClose();
    } catch (err) {
      console.error('Error connecting integration:', err);
    }
  });

  const {
    mutateAsync: disconnectIntegration,
    error: disconnectError,
    isLoading: isDisconnecting,
  } = useDisconnectIntegration();

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'modal-header' }}>
      <ModalHeader as="h2" id="modal-header">
        {integration.Label}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <IntegrationInfo integration={integration} website={website} helpUrl={helpUrl} />

        {connectError && <Alert variant="error" message={connectError.message} />}
        {disconnectError && <Alert variant="error" message={disconnectError.message} />}

        {integration.Status === StatusDTO.Broken && (
          <Alert
            variant="error"
            message="There was a problem connecting your account with Orion Planning. Please re-enter your credentials."
          />
        )}

        {integration.Status === StatusDTO.Success && (
          <Box display="flex" flexDirection="column" css={{ gap: '8px' }} mt="16">
            <Box display="flex" alignItems="center" css={{ gap: '8px' }}>
              <Text fontSize="h5">Connected</Text>
              <Icon className="fas fa-check" color="success.500" />
            </Box>

            <Text>Your {integration.Label} account is connected to Orion Planning.</Text>
          </Box>
        )}

        {integration.Status === StatusDTO.IsDownloading && (
          <Box display="flex" flexDirection="column" css={{ gap: '8px' }} mt="16">
            <Box display="flex" alignItems="center" css={{ gap: '8px' }}>
              <Text fontSize="h5">
                Your clients are currently being downloaded into Orion Planning. Refresh your
                browser after 5 minutes of connecting your account. If you do not see your clients,
                contact{' '}
                <Link href="mailto:financialplanning@orion.com">financialplanning@orion.com</Link>
              </Text>

              <Text fontSize="h5">
                Check your Notifications to see when the load is complete. For more information, go
                to the{' '}
                <Link href={helpUrl} target="_blank" rel="noreferrer noopener">
                  help page
                </Link>
              </Text>
              <Icon className="fas fa-check" color="success.500" />
            </Box>

            <Text>Your {integration.Label} account is connected to Orion Planning.</Text>
          </Box>
        )}

        {integration.Status !== StatusDTO.Success &&
          integration.Status !== StatusDTO.Broken &&
          integration.Status !== StatusDTO.IsDownloading && (
            <form onSubmit={onSubmit} id="integration-form">
              <Box mt="16" display="flex" flexDirection="column" css={{ gap: '8px' }}>
                <Text>
                  This will import your Blueleaf clients and their data into Orion Planning.
                </Text>
                <Input {...register('username')} label="Blueleaf API Token" />
              </Box>
            </form>
          )}
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        {integration.Status === StatusDTO.Success ||
        integration.Status === StatusDTO.IsDownloading ? (
          <Button
            ml="8"
            onClick={async () => {
              try {
                await disconnectIntegration(integration.Name);

                queryClient.invalidateQueries(queryKeys.integrations());
                onClose();
              } catch (err) {
                console.error('Error disconnecting integration:', err);
              }
            }}
            disabled={isDisconnecting}
            isLoading={isDisconnecting}
          >
            Disconnect {integration.Label}
          </Button>
        ) : (
          <Button
            ml="8"
            type="submit"
            form="integration-form"
            disabled={isConnecting}
            isLoading={isConnecting}
          >
            Connect to {integration.Label}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
